import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { CreateOrUpdateProviderRequest, ProviderResponse } from "./providersApi";
import { deleteProvider, updateProvider } from "./providersApi";
import { useProviderWithId } from "./useProviderWithId";

export const useProvider = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { providerId } = useParams<{ providerId: string }>();
  const { provider, isLoading } = useProviderWithId(providerId);

  const updateProviderMutation = useMutation<ProviderResponse, unknown, CreateOrUpdateProviderRequest>({
    mutationFn: (v) => updateProvider(providerId!, v),
    onSuccess: async (data) => {
      enqueueSnackbar(`Dostawca ${data.name} został zaktualizowany`, { variant: "success" });
      queryClient.setQueryData(["providers", providerId], data);
      queryClient.invalidateQueries({ queryKey: ["providers"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować dostawcy. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteProviderMutation = useMutation<void, unknown, { providerId: string }>({
    mutationFn: ({ providerId }) => deleteProvider(providerId),
    onSuccess: () => {
      enqueueSnackbar("Dostawca został usunięty", { variant: "success" });
      queryClient.removeQueries({ queryKey: ["providers", providerId] });
      queryClient.invalidateQueries({ queryKey: ["providers"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć dostawcy. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    provider,
    isLoading,
    updateProvider: (request: CreateOrUpdateProviderRequest) => updateProviderMutation.mutateAsync(request),
    deleteProvider: (providerId: string) => deleteProviderMutation.mutateAsync({ providerId }),
  };
};
